import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  Controller,
  useFieldArray,
  useForm,
} from "react-hook-form";

import { CurrencySelect } from "../../../components/currencies/CurrencySelect";

const fieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 38,
    borderRadius: 0,
    fontSize: "0.72rem",
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },

  "& .MuiFormHelperText-root": {
    mx: 0,
    fontSize: "0.63rem",
  },
};

const emptyRule = () => ({
  wifi_package_id: "",
  currency: "",
  minimum_amount: 0,
  is_active: true,
});

export function BulkShopWifiPackageRulesForm({
  shop,
  packages,
  busy,
  onCancel,
  onSubmit,
}) {
  const {
    control,
    handleSubmit,
    formState: {
      errors,
    },
  } = useForm({
    defaultValues: {
      rules: [
        emptyRule(),
      ],
    },
  });

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "rules",
  });

  const submit = (
    values,
  ) => {
    onSubmit(
      values.rules.map(
        (rule) => ({
          wifi_package_id:
            rule.wifi_package_id,

          currency:
            String(
              rule.currency,
            ).toUpperCase(),

          minimum_amount:
            Number(
              rule.minimum_amount,
            ),

          is_active:
            true,
        }),
      ),
    );
  };

  return (
    <Box
      component="form"
      onSubmit={
        handleSubmit(submit)
      }
    >
      {shop ? (
        <Paper
          variant="outlined"
          sx={{
            mb: 1.5,
            p: 1.3,
            borderRadius: 0,

            bgcolor:
              "#F8FAFC",
          }}
        >
          <Typography
            sx={{
              fontSize:
                "0.68rem",

              color:
                "#667085",

              textTransform:
                "uppercase",

              letterSpacing:
                "0.06em",

              fontWeight:
                800,
            }}
          >
            Shop
          </Typography>

          <Typography
            sx={{
              mt: 0.3,

              fontSize:
                "0.78rem",

              fontWeight:
                850,
            }}
          >
            {shop.shop_name}
          </Typography>

          <Typography
            sx={{
              mt: 0.15,

              color:
                "#667085",

              fontSize:
                "0.65rem",
            }}
          >
            {shop.shop_code}
          </Typography>
        </Paper>
      ) : null}

      <Stack
        spacing={1.2}
      >
        {fields.map(
          (
            field,
            index,
          ) => (
            <Paper
              key={
                field.id
              }
              variant="outlined"
              sx={{
                p: 1.3,
                borderRadius: 0,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize:
                      "0.7rem",

                    fontWeight:
                      850,
                  }}
                >
                  Rule{" "}
                  {index +
                    1}
                </Typography>

                {fields.length >
                1 ? (
                  <IconButton
                    size="small"
                    color="error"
                    disabled={
                      busy
                    }
                    onClick={() =>
                      remove(
                        index,
                      )
                    }
                  >
                    <DeleteOutlineRoundedIcon
                      sx={{
                        fontSize:
                          17,
                      }}
                    />
                  </IconButton>
                ) : null}
              </Stack>

              <Box
                sx={{
                  display:
                    "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md: "1fr 1fr",
                    },

                  gap: 1,
                }}
              >
                <Controller
                  name={`rules.${index}.wifi_package_id`}
                  control={
                    control
                  }
                  rules={{
                    required:
                      "Package is required.",
                  }}
                  render={({
                    field:
                      packageField,
                  }) => (
                    <TextField
                      {...packageField}
                      select
                      label="Wi-Fi package"
                      fullWidth
                      disabled={
                        busy
                      }
                      error={Boolean(
                        errors
                          .rules?.[
                          index
                        ]
                          ?.wifi_package_id,
                      )}
                      helperText={
                        errors
                          .rules?.[
                          index
                        ]
                          ?.wifi_package_id
                          ?.message
                      }
                      sx={
                        fieldSx
                      }
                    >
                      <MenuItem value="">
                        <em>
                          Select
                          package
                        </em>
                      </MenuItem>

                      {packages.map(
                        (
                          item,
                        ) => (
                          <MenuItem
                            key={
                              item.id
                            }
                            value={
                              item.id
                            }
                          >
                            {
                              item.name
                            }
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  )}
                />

                <Controller
                  name={`rules.${index}.currency`}
                  control={
                    control
                  }
                  rules={{
                    required:
                      "Currency is required.",
                  }}
                  render={({
                    field:
                      currencyField,
                  }) => (
                    <CurrencySelect
                      value={
                        currencyField.value
                      }
                      onChange={
                        currencyField.onChange
                      }
                      error={Boolean(
                        errors
                          .rules?.[
                          index
                        ]
                          ?.currency,
                      )}
                      helperText={
                        errors
                          .rules?.[
                          index
                        ]
                          ?.currency
                          ?.message
                      }
                      disabled={
                        busy
                      }
                    />
                  )}
                />

                <Controller
                  name={`rules.${index}.minimum_amount`}
                  control={
                    control
                  }
                  rules={{
                    required:
                      "Amount is required.",

                    min: {
                      value: 0,

                      message:
                        "Amount cannot be negative.",
                    },
                  }}
                  render={({
                    field:
                      amountField,
                  }) => (
                    <TextField
                      {...amountField}
                      type="number"
                      label="Minimum amount"
                      fullWidth
                      disabled={
                        busy
                      }
                      inputProps={{
                        min: 0,
                        step: "0.01",
                      }}
                      error={Boolean(
                        errors
                          .rules?.[
                          index
                        ]
                          ?.minimum_amount,
                      )}
                      helperText={
                        errors
                          .rules?.[
                          index
                        ]
                          ?.minimum_amount
                          ?.message
                      }
                      sx={
                        fieldSx
                      }
                    />
                  )}
                />
              </Box>
            </Paper>
          ),
        )}
      </Stack>

      <Button
        type="button"
        variant="outlined"
        startIcon={
          <PlaylistAddRoundedIcon />
        }
        onClick={() =>
          append(
            emptyRule(),
          )
        }
        disabled={busy}
        sx={{
          mt: 1.4,
          borderRadius: 0,
        }}
      >
        Add another rule
      </Button>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{
          mt: 2,
        }}
      >
        <Button
          type="button"
          color="inherit"
          variant="outlined"
          onClick={
            onCancel
          }
          disabled={busy}
          sx={{
            borderRadius: 0,
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={
            busy ||
            !packages.length
          }
          sx={{
            borderRadius: 0,
          }}
        >
          {busy
            ? "Saving rules..."
            : "Create rules"}
        </Button>
      </Stack>
    </Box>
  );
}